import React from 'react';
import {Box, styled} from '@mui/material';
import {Avatar} from '@contentlab/api/avatars';
import {
  AvatarSize,
  AvatarSpecConfig,
  DraftAiAvatarProject,
} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import ElementPosition from '@contentlab/types/elementPosition';
import {RgbaColor} from '@/types/rgbaColor';
import {Asset} from '@/views/content-studio/components/asset-table/types';
import useAssetQuery from '@/hooks/content-studio/use-asset-query';

const AVATAR_ASPECT_RATIO = 9 / 16;

const AVATAR_MARGIN_PERCENTAGE = 0.025;
const AVATAR_MASK_TOP_MARGIN_PERCENTAGE = 0.073;

const AVATAR_SIZE_PERCENTAGE = {
  [AvatarSize.SMALL]: 0.25,
  [AvatarSize.MEDIUM]: 0.3,
  [AvatarSize.LARGE]: 0.35,
};

const AvatarContainer = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  pointerEvents: 'none',
  color: 'red',
}));

const AvatarStyle = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'avatarWidth',
      'avatarHeight',
      'cropAvatar',
      'circleMask',
      'backgroundColor',
      'imageUrl',
      'avatarMaskOffsetPx',
    ].includes(prop as string),
})<{
  avatarWidth: number;
  avatarHeight: number;
  cropAvatar: boolean;
  circleMask: boolean;
  backgroundColor: RgbaColor | null;
  imageUrl: string;
  avatarMaskOffsetPx: number;
}>(({
  avatarWidth,
  avatarHeight,
  cropAvatar,
  circleMask,
  backgroundColor,
  imageUrl,
  avatarMaskOffsetPx,
}) => {
  const maskImage = circleMask
    ? 'radial-gradient(circle closest-side, black 100%, transparent 100%)'
    : 'linear-gradient(to bottom, black 0%, black 100%)';

  const maskSize = `100% ${AVATAR_ASPECT_RATIO * 100}%`;

  return {
    position: 'absolute',
    width: avatarWidth,
    height: avatarHeight,
    background: `url(${imageUrl}) center/cover no-repeat`,
    pointerEvents: 'none',
    backgroundColor: backgroundColor
      ? `rgba(${backgroundColor.r}, ${backgroundColor.g}, ${backgroundColor.b}, ${backgroundColor.a})`
      : 'transparent',
    ...((cropAvatar || circleMask) && {
      WebkitMaskImage: maskImage,
      maskImage,
      WebkitMaskSize: maskSize,
      maskSize,
      WebkitMaskPosition: `center ${avatarMaskOffsetPx}px`,
      maskPosition: `center ${avatarMaskOffsetPx}px`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }),
  };
});

const getAvatarPositionProps = (
  position: ElementPosition,
  videoLongerSidePx: number,
  avatarHeight: number,
  avatarWidth: number,
  isCropped: boolean,
  avatarMaskOffsetPx: number
) => {
  const avatarMarginPx = videoLongerSidePx * AVATAR_MARGIN_PERCENTAGE;
  const croppedAvatarTopMarginPxAdjustment = isCropped ? avatarMaskOffsetPx : 0;
  const croppedAvatarBottomMarginPxAdjustment = isCropped
    ? -(avatarHeight - (avatarWidth + avatarMaskOffsetPx))
    : 0;

  switch (position) {
    case ElementPosition.TOP_LEFT:
      return {
        top: avatarMarginPx - croppedAvatarTopMarginPxAdjustment,
        left: 0,
      };
    case ElementPosition.TOP_CENTER:
      return {
        top: avatarMarginPx - croppedAvatarTopMarginPxAdjustment,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case ElementPosition.TOP_RIGHT:
      return {
        top: avatarMarginPx - croppedAvatarTopMarginPxAdjustment,
        right: 0,
      };
    case ElementPosition.MIDDLE_LEFT:
      return {
        top: `calc(50% + ${croppedAvatarBottomMarginPxAdjustment}px)`,
        left: 0,
        transform: `translateY(${isCropped ? '0%' : '-50%'})`,
      };
    case ElementPosition.MIDDLE_CENTER:
      return {
        top: `calc(50% + ${croppedAvatarBottomMarginPxAdjustment}px)`,
        left: '50%',
        transform: `translateX(-50%) translateY(${isCropped ? '0%' : '-50%'})`,
      };
    case ElementPosition.MIDDLE_RIGHT:
      return {
        top: `calc(50% + ${croppedAvatarBottomMarginPxAdjustment}px)`,
        right: 0,
        transform: `translateY(${isCropped ? '0%' : '-50%'})`,
      };
    case ElementPosition.BOTTOM_LEFT:
      return {
        left: 0,
        bottom: croppedAvatarBottomMarginPxAdjustment,
      };
    case ElementPosition.BOTTOM_CENTER:
      return {
        left: '50%',
        bottom: croppedAvatarBottomMarginPxAdjustment,
        transform: 'translateX(-50%)',
      };
    case ElementPosition.BOTTOM_RIGHT:
      return {
        right: 0,
        bottom: croppedAvatarBottomMarginPxAdjustment,
      };
    default:
      return {
        right: 0,
      };
  }
};

const getAvatarDimensions = (
  avatarSize: AvatarSize,
  videoHeightPx: number,
  videoLongerSidePx: number
) => {
  let avatarWidth = videoLongerSidePx * AVATAR_SIZE_PERCENTAGE[avatarSize];
  const avatarHeight = avatarWidth * (1 / AVATAR_ASPECT_RATIO);
  if (avatarHeight <= videoHeightPx) {
    return {avatarWidth, avatarHeight};
  }
  avatarWidth = videoHeightPx * AVATAR_ASPECT_RATIO;
  return {avatarWidth, avatarHeight: videoHeightPx};
};

function getImageUrl(
  libraryAvatar: Avatar | null,
  customImageAsset: Asset | undefined,
  avatarKind: AvatarKind,
  avatarSpec: DraftAiAvatarProject['avatarSpec'],
  activeConfig: AvatarSpecConfig
) {
  if (avatarKind === AvatarKind.LIBRARY && libraryAvatar) {
    return activeConfig.removeBackground
      ? libraryAvatar.thumbnailNoBackground
      : libraryAvatar.thumbnail;
  }
  if (avatarKind === AvatarKind.CUSTOM && avatarSpec.imagePreviewUrl) {
    return avatarSpec.imagePreviewUrl;
  }
  return customImageAsset?.url || customImageAsset?.thumbnailUrl || null;
}

interface AvatarOverlayProps {
  selectedAvatarKind: AvatarKind;
  avatarSpec: DraftAiAvatarProject['avatarSpec'];
  selectedAvatar: Avatar;
  videoDisplayDimensions: {width: number; height: number};
}

function AvatarOverlay({
  selectedAvatarKind,
  avatarSpec,
  selectedAvatar,
  videoDisplayDimensions,
}: AvatarOverlayProps) {
  const {data: customImageAsset} = useAssetQuery({
    id: avatarSpec.imageAssetId ? String(avatarSpec.imageAssetId) : '',
    enabled:
      Boolean(avatarSpec.imageAssetId) &&
      selectedAvatarKind === AvatarKind.CUSTOM &&
      !avatarSpec.imagePreviewUrl,
  });

  if (
    selectedAvatarKind === AvatarKind.VOICE_ONLY ||
    (!avatarSpec.imageAssetId && !avatarSpec.libraryId)
  )
    return null;

  // Determine active config based on avatar type
  const activeConfig = (() => {
    const base = avatarSpec.config;
    if (selectedAvatarKind === AvatarKind.LIBRARY) {
      return avatarSpec.configLibrary || base;
    }
    if (selectedAvatarKind === AvatarKind.CUSTOM) {
      return avatarSpec.configCustom || base;
    }
    return base;
  })();

  const imageUrl = getImageUrl(
    selectedAvatar,
    customImageAsset,
    selectedAvatarKind,
    avatarSpec,
    activeConfig
  );

  const videoLongerSidePx =
    videoDisplayDimensions.width >= videoDisplayDimensions.height
      ? videoDisplayDimensions.width
      : videoDisplayDimensions.height;

  const {avatarWidth, avatarHeight} = getAvatarDimensions(
    activeConfig.avatarSize,
    videoDisplayDimensions.height,
    videoLongerSidePx
  );

  const avatarMaskOffsetPx = avatarHeight * AVATAR_MASK_TOP_MARGIN_PERCENTAGE;

  const avatarPositionProps = getAvatarPositionProps(
    activeConfig.avatarPosition,
    videoLongerSidePx,
    avatarHeight,
    avatarWidth,
    activeConfig.cropAvatar || activeConfig.circleMask,
    avatarMaskOffsetPx
  );

  return (
    <AvatarContainer>
      {imageUrl && (
        <AvatarStyle
          avatarWidth={avatarWidth}
          avatarHeight={avatarHeight}
          cropAvatar={activeConfig.cropAvatar}
          circleMask={activeConfig.circleMask}
          backgroundColor={activeConfig.avatarColor}
          imageUrl={imageUrl}
          avatarMaskOffsetPx={avatarMaskOffsetPx}
          sx={avatarPositionProps}
        />
      )}
    </AvatarContainer>
  );
}

export default AvatarOverlay;
